import React, { useMemo, useContext } from "react";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { IChartBar, IFailureTypes, IReportsByMachineCenter } from "./MachineCentersInterfaces";
import { BarsChart } from "../../BarChart/BarsChart.view";
import { Box, Typography } from "@mui/material";
import { useStyles } from "./MachineCenters.styles";
import { useTranslation } from "react-i18next";
import NoData from "components/NoData/NoData.view";
import { IEventFailureCodeSummaryPerMC } from "entities/Mills/MillsEntityInterfaces";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReport/components/SummaryReportFilters/useMillFilters";

export const MachineCenters = () => {
    const { millId } = useParams();
    const { filters } = useContext(SummaryReportDashboardContext);
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
            displayIn: filters.displayIn ? filters.displayIn : initialMillFilters.displayIn,
        }),
        [filters],
    );
    const { machineCentersDowntimesByMill } = MillsEntity.GetMachineCentersDowntimesByMill(+millId!, queryFilters);
    const classes = useStyles();
    const { t } = useTranslation();

    const dataSortedByMachineCenter: { [key: string]: IReportsByMachineCenter } = useMemo(() => {
        const statsObject: { [key: string]: IReportsByMachineCenter } = {};

        machineCentersDowntimesByMill.forEach((stat: IEventFailureCodeSummaryPerMC) => {
            const newFailureType = {
                failure_type_name: stat.failure_type.name,
                failure_type_color: stat.failure_type.color_code,
                failure_type_total_minutes: stat.seconds,
                failure_type_total_occurrences: stat.occurrences,
            };

            if (Object.keys(statsObject).includes(stat.machine_center.name)) {
                const existingFailureType = statsObject[stat.machine_center.name].failure_types.find(
                    (failureType) => failureType.failure_type_name === newFailureType.failure_type_name,
                );

                if (existingFailureType) {
                    existingFailureType.failure_type_total_minutes += newFailureType.failure_type_total_minutes;
                    existingFailureType.failure_type_total_occurrences += newFailureType.failure_type_total_occurrences;
                } else {
                    statsObject[stat.machine_center.name].failure_types.push(newFailureType);
                }
            } else {
                const values: IReportsByMachineCenter = {
                    machine_center_name: stat.machine_center.name,
                    machine_center_short_name: stat.machine_center.short_name ?? stat.machine_center.name,
                    machine_center_id: stat.machine_center.machine_center_id,
                    failure_types: [newFailureType],
                };

                statsObject[values.machine_center_name] = values;
            }

            statsObject[stat.machine_center.name].total_minutes = statsObject[
                stat.machine_center.name
            ].failure_types.reduce(
                (acc: number, current: IFailureTypes) => acc + current.failure_type_total_minutes,
                0,
            );

            statsObject[stat.machine_center.name].total_occurrences = statsObject[
                stat.machine_center.name
            ].failure_types.reduce(
                (acc: number, current: IFailureTypes) => acc + current.failure_type_total_occurrences,
                0,
            );
        });

        Object.values(statsObject).forEach((stat) => {
            stat.total_minutes = Math.round(stat.total_minutes! / 60) < 1 ? 1 : Math.round(stat.total_minutes! / 60);
            stat.failure_types.forEach(
                (failureType) =>
                    (failureType.failure_type_total_minutes =
                        Math.round(failureType.failure_type_total_minutes! / 60) < 1
                            ? 1
                            : Math.round(failureType.failure_type_total_minutes! / 60)),
            );
        });

        return statsObject;
    }, [machineCentersDowntimesByMill]);

    const machineCentersByOccurrences: IReportsByMachineCenter[] = useMemo(
        () =>
            Object.values(dataSortedByMachineCenter)
                .filter((item) => item.total_occurrences && item.total_occurrences > 0)
                .sort((a, b) => b.total_occurrences! - a.total_occurrences!)
                .slice(0, 5),
        [dataSortedByMachineCenter],
    );

    const machineCentersByMinutes: IReportsByMachineCenter[] = useMemo(
        () =>
            Object.values(dataSortedByMachineCenter)
                .filter((item) => item.total_minutes && item.total_minutes > 0)
                .sort((a, b) => b.total_minutes! - a.total_minutes!)
                .slice(0, 5),
        [dataSortedByMachineCenter],
    );

    const barsChartData = useMemo(
        () => [
            queryFilters.displayIn === "minutes"
                ? machineCentersByMinutes.map((stat) => {
                      const chartBar: IChartBar = {
                          machineCenter: stat.machine_center_short_name,
                      };

                      stat.failure_types.forEach((item) => {
                          chartBar[`${item.failure_type_name}Color`] = item.failure_type_color;
                          chartBar[`${item.failure_type_name}total_minutes`] = item.failure_type_total_minutes;
                      });

                      return chartBar;
                  })
                : machineCentersByOccurrences.map((stat) => {
                      const chartBar: IChartBar = {
                          machineCenter: stat.machine_center_short_name,
                      };

                      stat.failure_types.forEach((item) => {
                          chartBar[`${item.failure_type_name}Color`] = item.failure_type_color;
                          chartBar[`${item.failure_type_name}total_occurrences`] = item.failure_type_total_occurrences;
                      });

                      return chartBar;
                  }),
        ],
        [queryFilters.displayIn, machineCentersByMinutes, machineCentersByOccurrences],
    );

    return (
        <Box className={classes.container} sx={{ flexDirection: "column" }}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title} variant="subtitle1">
                    {t("locations.summary_report.top_five_bad_actors.machine_centers.title")}
                </Typography>
            </Box>
            {!barsChartData[0].length ? (
                <Box className={classes.noData}>
                    <NoData />
                </Box>
            ) : (
                <Box className={classes.graphContainer}>
                    <Box className={classes.graphGridItemContainer}>
                        <BarsChart data={barsChartData[0]} index="machineCenter" orientation="vertical" />
                    </Box>
                </Box>
            )}
        </Box>
    );
};
